import { Link, NavLink } from 'react-router'
import { KeyRound, LogOut, MonitorSmartphone, UserRound, Users } from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { User } from '@/api/types'
import { cn } from '@/lib/utils'

// 사용자의 상단 바. 화면이 내 VM 과 팀 둘이라 그 링크 둘과 계정 메뉴(내 정보 · SSH 키 · 로그아웃)만 있다.
// 답을 기다리는 초대(F-92)가 있으면 `팀` 링크에 그 수가 선다 — 관리자 상단 바의 검토 대기 수와 같은 모양이다.
// 좁아지면 아이디가 접힌다(계정 메뉴는 남는다).

const NAV = 'flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] text-muted-foreground hover:text-foreground'

export function UserTopbar({ user, invites, onLogout }: { user: User; invites: number; onLogout: () => void }) {
  return (
    <header className="flex h-12 flex-none items-center gap-3 border-b bg-card px-4">
      <Link to="/" className="flex items-center gap-2 text-[13px] font-semibold">
        <MonitorSmartphone className="size-4 text-brand" />
        LCCS
      </Link>
      <nav className="flex items-center gap-1">
        <NavLink to="/" end className={({ isActive }) => cn(NAV, isActive && 'bg-surface-2 text-foreground')}>
          <MonitorSmartphone className="size-3.5" />내 VM
        </NavLink>
        <NavLink
          to="/teams"
          aria-label={invites > 0 ? `팀 · 받은 초대 ${invites}건` : '팀'}
          className={({ isActive }) => cn(NAV, isActive && 'bg-surface-2 text-foreground')}
        >
          <Users className="size-3.5" />팀
          {invites > 0 ? (
            <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-warn px-1 text-[10px] font-semibold text-black">
              {invites}
            </span>
          ) : null}
        </NavLink>
      </nav>
      <div className="flex-1" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-9 gap-2 px-2 hover:bg-surface-2" aria-label={`${user.username} 계정`}>
            <Avatar className="size-7">
              <AvatarFallback className="bg-brand text-[11px] font-semibold text-white">
                {user.username.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="text-[13px] max-sm:hidden">{user.username}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem asChild>
            <NavLink to="/me">
              <UserRound className="size-3.5" />
              내 정보
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <NavLink to="/me/keys">
              <KeyRound className="size-3.5" />
              SSH 키
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onLogout}>
            <LogOut className="size-3.5" />
            로그아웃
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  )
}
